// /* eslint-disable */
// import { ApolloClient, ApolloLink, ApolloProvider, HttpLink, InMemoryCache, Observable, split } from "@apollo/client";
// import { setContext } from "@apollo/client/link/context";
// import { onError } from "@apollo/client/link/error";
// import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
// import { getMainDefinition } from "apollo-utilities";
// import { createClient } from "graphql-ws";
// import { createContext, useContext, useEffect, useState } from "react";
// import { UseRefreshToken } from "../hooks/auth";
// import { deleteLocalTokens, getLocalTokens } from "../utils/session";

// const ApolloContext = createContext(undefined);

// export const CustomApolloProvider = ({ children }) => {
//   const [apolloClient, setApolloClient] = useState(null);
//   const [isError, setIsError] = useState(false);
//   // const backendAddress = `${backendProtocol}://${backendHost}:${backendPort}${backendGraphql}`;

//   const getAuthToken = () => {
//     const tokens = getLocalTokens();
//     return tokens?.accessToken;
//   };

//   const httpLink = new HttpLink({
//     uri: `${process.env.REACT_APP_BACKEND_URI}`,
//   });

//   const wsLink = new GraphQLWsLink(
//     createClient({
//       url: process.env.REACT_APP_WSS_URI,
//       connectionParams: () => {
//         return {
//           authorization: `Bearer ${getAuthToken()}`,
//         };
//       },
//     }),
//   );

//   const authMiddleware = new ApolloLink((operation, forward) => {
//     const tokens = getLocalTokens();
//     const token = operation.operationName === "RefreshToken" ? tokens.refreshToken : tokens.accessToken;
//     operation.setContext(({ headers = {} }) => ({
//       headers: {
//         ...headers,
//         authorization: `Bearer ${token}`,
//       },
//     }));

//     return forward(operation);
//   });

//   const authLink = setContext((operation, prevContext) => {
//     const tokens = getLocalTokens();
//     const token = operation.operationName === "RefreshToken" ? tokens.refreshToken : tokens.accessToken;

//     if (!token) {
//       return prevContext;
//     }

//     return {
//       headers: {
//         ...prevContext.headers,
//         authorization: `Bearer ${token}`,
//       },
//     };
//   });

//   const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
//     const tokens = getLocalTokens();

//     if (!graphQLErrors) {
//       return;
//     }

//     if (!tokens.refreshToken && !tokens.accessToken) {
//       return;
//     }

//     if (graphQLErrors && graphQLErrors[0]?.extensions?.code === "UNAUTHENTICATED" && operation?.operationName === "RefreshToken") {
//       return;
//     }

//     if (graphQLErrors && graphQLErrors[0]?.extensions?.code !== "UNAUTHENTICATED") {
//       return;
//     }

//     return new Observable(observer => {
//       (async () => {
//         try {
//           const accessToken = await UseRefreshToken(tokens);

//           const oldHeaders = operation.getContext().headers;
//           operation.setContext({
//             headers: {
//               ...oldHeaders,
//               authorization: `Bearer ${accessToken}`,
//             },
//           });

//           const subscriber = {
//             next: observer.next.bind(observer),
//             error: observer.error.bind(observer),
//             complete: observer.complete.bind(observer),
//           };

//           forward(operation).subscribe(subscriber);
//         } catch (err) {
//           deleteLocalTokens();
//           setIsError(true);
//           console.error("Missing Token");
//         }
//       })();
//     });
//   });

//   const wslinks = split(
//     ({ query }) => {
//       const { kind, operation } = getMainDefinition(query);
//       return kind === "OperationDefinition" && operation === "subscription";
//     },
//     wsLink,
//     httpLink,
//   );
//   const apolloLink = ApolloLink.from([authLink, errorLink, wslinks]);

//   useEffect(() => {
//     setApolloClient(
//       new ApolloClient({
//         link: apolloLink,
//         cache: new InMemoryCache(),
//       }),
//     );
//   }, []);
//   return <ApolloContext.Provider value={{ isError, apolloClient }}>{apolloClient && <ApolloProvider client={apolloClient}>{children}</ApolloProvider>}</ApolloContext.Provider>;
// };

// export const useApolloContext = () => {
//   const context = useContext(ApolloContext);
//   if (!context) throw new Error("useApolloContext must be used within a CustomApolloProvider");
//   return context;
// };

/* eslint-disable */
import { ApolloClient, ApolloLink, ApolloProvider, HttpLink, Observable, split } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "apollo-utilities";
import { createClient } from "graphql-ws";
import { createContext, useContext, useEffect, useState } from "react";
import { persistCache, SessionStorageWrapper } from "apollo3-cache-persist";

// You need a separate file for the cache instance so it can be imported elsewhere
// For example, create src/apollo/cache.js and export the cache from there.
import { cache } from "./cache";
import { UseRefreshToken } from "../hooks/auth";
import { deleteLocalTokens, getLocalTokens } from "../utils/session";

const ApolloContext = createContext(undefined);

// Module-level so it's shared across every concurrent 401 across the whole
// app, not per-render/per-effect. Backend refresh tokens are single-use
// (rotated on every call), so if several requests hit UNAUTHENTICATED at
// once (common right when the access token expires and several components
// refetch on mount), firing one UseRefreshToken() per request would let
// only the first succeed — the rest would refresh with an already-invalidated
// token, get rejected, and spuriously log the user out. This ensures only
// one refresh call is ever in flight; concurrent callers await the same
// promise and all retry with its result.
let inFlightRefresh = null;
const getSharedRefreshToken = tokens => {
  if (!inFlightRefresh) {
    inFlightRefresh = UseRefreshToken(tokens).finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
};

export const CustomApolloProvider = ({ children }) => {
  const [apolloClient, setApolloClient] = useState(null);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const initApollo = async () => {
      // Wait for the cache to be restored from sessionStorage before client creation
      await persistCache({
        cache,
        storage: new SessionStorageWrapper(window.sessionStorage),
      });

      // All your existing link setup logic
      const httpLink = new HttpLink({
        uri: process.env.REACT_APP_BACKEND_URI,
      });

      const wsLink = new GraphQLWsLink(
        createClient({
          url: process.env.REACT_APP_WSS_URI,
          connectionParams: () => {
            const tokens = getLocalTokens();
            return {
              authorization: `Bearer ${tokens?.accessToken}`,
            };
          },
        }),
      );

      const authLink = setContext((operation, prevContext) => {
        const tokens = getLocalTokens();
        const token = operation.operationName === "RefreshToken" ? tokens?.refreshToken : tokens?.accessToken;
        if (!token) return prevContext;
        return {
          headers: { ...prevContext.headers, authorization: `Bearer ${token}` },
        };
      });

      const errorLink = onError(({ graphQLErrors, operation, forward }) => {
        if (graphQLErrors) {
          for (let err of graphQLErrors) {
            if (err.extensions?.code === "UNAUTHENTICATED") {
              if (operation.operationName === "RefreshToken") return;
              const tokens = getLocalTokens();
              if (!tokens?.refreshToken) {
                deleteLocalTokens();
                setIsError(true);
                return;
              }
              return new Observable(observer => {
                (async () => {
                  try {
                    const accessToken = await getSharedRefreshToken(tokens);
                    operation.setContext(({ headers = {} }) => ({
                      headers: { ...headers, authorization: `Bearer ${accessToken}` },
                    }));
                    forward(operation).subscribe(observer);
                  } catch (err) {
                    deleteLocalTokens();
                    setIsError(true);
                    observer.error(err);
                  }
                })();
              });
            }
          }
        }
      });

      const splitLink = split(
        ({ query }) => {
          const { kind, operation } = getMainDefinition(query);
          return kind === "OperationDefinition" && operation === "subscription";
        },
        wsLink,
        httpLink
      );
      
      const apolloLink = ApolloLink.from([errorLink, authLink, splitLink]);

      // Create the client with the persisted cache
      const client = new ApolloClient({
        link: apolloLink,
        cache, // Use the persisted cache instance here
      });

      // Set the ready client to state, which will trigger a re-render
      setApolloClient(client);
    };

    initApollo();
  }, []); // Empty array ensures this runs only once

  // Render a loading state while the client is being initialized
  if (!apolloClient) {
    return <div>Loading...</div>; // Or a more sophisticated loader component
  }

  // Once ready, provide the client to the rest of the app
  return (
    <ApolloContext.Provider value={{ isError, apolloClient }}>
      <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
    </ApolloContext.Provider>
  );
};

export const useApolloContext = () => {
  const context = useContext(ApolloContext);
  if (!context) throw new Error("useApolloContext must be used within a CustomApolloProvider");
  return context;
};
